import { PageEnum } from "@/enums";
import {
    type EndPageEnum,
    type EnvironmentVariablesModel,
    EventTypeEnum,
    type GlobalModel,
    AdobeEventTypeEnum,
} from "@/models";
import { defineStore } from "pinia";
import { useDataLayerStore } from "@/stores";
import axios from "axios";

export const useGlobalStore = defineStore("global", {
    state: (): GlobalModel => {
        return {
            activeSection: PageEnum.GetQuote,
            showDelayModal: false,
            contactComplete: false,
            currentPromo: [],
            showForm: true,
            step: 1,
            endPage: null,
            delay: 0,
            tick: null,
            incentiveAmount: 100,
            env: {
                API_URL: "",
                ASANTO_TARGET: "",
                DATALAYER_URL: "",
                SENTRY_DSN: "",
                SHOW_CALLBACK_REQUEST: "false",
                SYSTEM_MAINTENANCE: "false",
                SYSTEM_MAINTENANCE_MSG: "",
            },
        };
    },
    getters: {
        systemMaintenance(state): boolean {
            return (
                state.env.SYSTEM_MAINTENANCE === "true" ||
                state.env.SYSTEM_MAINTENANCE === true
            );
        },
        systemMaintenanceMessage(state): string {
            return state.env.SYSTEM_MAINTENANCE_MSG;
        },
        showCallBackRequestForm(state): boolean {
            return (
                state.env.SHOW_CALLBACK_REQUEST === "true" ||
                state.env.SHOW_CALLBACK_REQUEST === true
            );
        },
        dataLayer: () => {
            return useDataLayerStore();
        },
        years: (): number[] => {
            const yearMin = new Date().getFullYear() - 50;
            const yearMax = new Date().getFullYear() - 81;
            const yearArray = [];
            for (let year = yearMax; year <= yearMin; year++) {
                yearArray.push(year);
            }
            return yearArray;
        },
        checkForIncentive: (state: GlobalModel): boolean => {
            return state.incentiveAmount > 0;
        },
    },
    actions: {
        async getEnvironment(): Promise<void> {
            const { data } = await axios<EnvironmentVariablesModel>({
                method: "get",
                url: "/environment",
                headers: { "Content-Type": "application/json" },
            });
            this.env = typeof data === "string" ? JSON.parse(data) : data;
        },
        startTick(): void {
            this.dataLayer.triggerEvent(EventTypeEnum.Loading);
            this.tick = setInterval(() => {
                this.delay += 500;
                if (this.delay >= 2500 && !this.showDelayModal) {
                    this.showDelayModal = true;
                    this.dataLayer.triggerEvent(EventTypeEnum.Loading, "Y");
                }
            }, 500);
        },
        resetTick(): void {
            clearInterval(this.tick as ReturnType<typeof setInterval>);
            this.tick = null;
            this.delay = 0;
            if (!this.showDelayModal) {
                this.dataLayer.triggerEvent(EventTypeEnum.Loading, "N");
            } else {
                this.showDelayModal = false;
            }
        },
        hideForm(endPage: EndPageEnum): void {
            this.endPage = endPage;
            this.showForm = false;
        },
        getPageStepNumber: (page: PageEnum): number => {
            switch (page) {
                case PageEnum.GetQuote:
                    return 1;
                case PageEnum.SelectCover:
                    return 2;
                case PageEnum.CoverSummary:
                    return 3;
                case PageEnum.YourDetails:
                    return 4;
                case PageEnum.PaymentDetails:
                    return 5;
                case PageEnum.YourPlan:
                    return 6;
                case PageEnum.ConfirmationPage:
                    return 7;
                case PageEnum.ErrorPage:
                    return 8;
                default:
                    return 1;
            }
        },
        gotoPage(page: PageEnum): void {
            this.activeSection = page;
            this.step = this.getPageStepNumber(page);
            this.dataLayer.pageNumber = this.step;

            setTimeout(() => {
                document
                    .querySelector(`#${page}`)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 200);

            this.dataLayer.triggerAdobeEvent(AdobeEventTypeEnum.PageView);
        },
        formatNumber(number: number, currency: boolean = false): string {
            const numberString = number.toFixed(0).toString();

            const x = numberString.split(".");
            let x1 = x[0];
            const x2 = x.length > 1 ? `.${x[1]}` : "";

            const re = /(\d+)(\d{3})/;

            while (re.test(x1)) {
                x1 = x1.replace(re, "$1" + "," + "$2");
            }

            return currency ? `£${x1 + x2}` : x1 + x2;
        },
        leftPad(value: number): string {
            return `0${value}`.slice(-2);
        },
    },
});
